import {
  Children,
  cloneElement,
  Fragment,
  isValidElement,
  useMemo,
} from 'react'

interface IUseBannerElementsProps {
  visibleCards: number
  infiniteLoop: boolean
  children: React.ReactNode
  childrenLength: number
}

/**
 * Flattens Fragment wrappers so every banner element is reachable.
 *
 * Consumers pass their banners wrapped in a single Fragment. Without
 * flattening, `Children.toArray` yields that one Fragment and `cloneElement`
 * targets it — and a Fragment silently drops every prop except `key`, so
 * `isClone` never reaches the banners (FP-4222).
 */
const flattenBanners = (children: React.ReactNode): React.ReactNode[] =>
  Children.toArray(children).flatMap((child) =>
    isValidElement(child) && child.type === Fragment
      ? flattenBanners((child.props as { children?: React.ReactNode }).children)
      : [child]
  )

const cloneAsFiller = (
  banner: React.ReactNode,
  key: string,
  markClone: boolean
): React.ReactNode => {
  if (!isValidElement(banner)) return null

  return cloneElement(banner, {
    key,
    // Marks this as an infinite-loop filler clone (see Banner.tsx). Clones must
    // not register trailer slots or fire visibility callbacks.
    ...(markClone ? { isClone: true } : null),
  } as Partial<unknown> & React.Attributes)
}

/**
 * Builds the infinite-loop carousel track:
 * `[last visibleCards clones, ...banners, first visibleCards clones]`.
 *
 * This layout is what `getBannerContentIndexFromSlide` and
 * `useBannerRowScrollTransition` expect, and it pairs with an initial
 * `activeSlide` of `visibleCards` (see `useBannerScroll`).
 */
export const useBannerElements = ({
  visibleCards,
  infiniteLoop,
  children,
  childrenLength,
}: IUseBannerElementsProps) => {
  const banners = useMemo(() => {
    if (!infiniteLoop) return undefined

    const list = flattenBanners(children)
    if (!list.length) return undefined

    // `isClone` is positional: it means "this element is a filler duplicate the
    // track never shows". That only holds when each child is one banner. A
    // skin-driven carousel passes a single element that expands into every
    // banner, so its clone covers the whole group — including the banner the
    // track does show — and marking it would silence that banner's trailer.
    const markClone = list.length === childrenLength

    const leading: React.ReactNode[] = []
    const trailing: React.ReactNode[] = []

    for (let index = 0; index < visibleCards; index++) {
      leading.push(
        cloneAsFiller(
          list[list.length - visibleCards + index],
          `banner_leading_${index}`,
          markClone
        )
      )
      trailing.push(
        cloneAsFiller(list[index], `banner_trailing_${index}`, markClone)
      )
    }

    return [...leading, ...list, ...trailing]
  }, [children, childrenLength, infiniteLoop, visibleCards])

  return { banners }
}
